import {
    DotLegalButton,
    DotLegalCheckbox,
    DotLegalEmptyState,
    DotLegalMultiSelect,
    DotLegalPaper,
    DotLegalTable,
    DotLegalTooltip,
    ITableHeader,
    useDotLegalSnackbar,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import {
    AuditGroupLegalEntityContact,
    AuditGroupLegalEntityRowApiModel,
    AuditGroupLegalEntityRowViewModel,
    LegalEntityAuditInformationViewModel,
} from "./AuditGroupLegalEntitySelector.types";
import { Box, Skeleton, TableCell, Typography } from "@mui/material";
import useAuditGroupLegalEntitySelectorHooks from "./AuditGroupLegalEntitySelector.hooks";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import FilterBar from "@common/components/filterBar/FilterBar";
import DotLegalSelectWithButton from "../../../common/components/dotLegalSelectWithButton/DotLegalSelectWithButton";
import { DotLegalSelectOption } from "../../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { setFirstLetterToLowerCase } from "../../../common/stringOperations";
import { LegalEntityContactType } from "../../../legalEntity/legalEntityContactTab/LegalEntityContactTab.types";
import { getAdditionalRecipientOptions } from "../../../legalEntity/legalEntityAuditTab/auditAdditionalRecipients.utils";
import React from "react";
import { LegalEntityAuditGroupDraft } from "../LegalEntityAuditGroupOverview.types";
import DotLegalLink from "../../../common/components/dotLegalLink/DotLegalLink";
import { useUrlProvider } from "../../../useUrlProvider";
import { LegalEntityAuditGroupSendModel } from "../sendAuditsDialog/SendAuditsDialog.types";
import ClassificationRisk from "../../../common/components/questionnaireComponents/classiificationRisk/ClassificationRisk";
import { LegalEntityScope } from "../../../legalEntity/legalEntities/LegalEntities.types";
import SendAuditsDialog from "../sendAuditsDialog/SendAuditsDialog";
import NoData from "../../../legalEntity/legalEntityTable/noData.svg?react";
import { LegalEntityAuditFrequency, LegalEntityAuditType } from "../../../legalEntity/LegalEntity.types";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";
import TrialUsageWarning from "../../../common/components/TrialUsageWarning";
import TrialExpiredRowWrapper from "../../../common/components/TrialExpiredRowWrapper";
import PlanDialogButtonWrapper from "../../../plan/planDialogWrapper/planDialogWrapper";
import { WhiteLockIcon } from "../../../common/icons";
import { useLegalEntityTypeNames } from "../../../legalEntity/useLegalEntityTypeNames.ts";

export interface IAuditGroupLegalEntitySelector {
    selectedEntities: Array<LegalEntityAuditGroupDraft>;
    onSelectedEntitiesUpdated: (selectedEntities: Array<LegalEntityAuditGroupDraft>) => void;
    selectedEntititesIsLoading: boolean;
    rows: Array<AuditGroupLegalEntityRowApiModel>;
    onContactCreated: () => void;
    auditGroupId: string;
    auditGroupName: string;
    isDeadlineOverdue: boolean;
}

function AuditGroupLegalEntitySelector(props: IAuditGroupLegalEntitySelector) {
    const { translateString, translateCountry, translateDate } = useTranslation();
    const { getLegalEntityTypeName } = useLegalEntityTypeNames();
    const { getLegalEntityUrl } = useUrlProvider();
    const { vendorManagementFeatures } = usePlanContext();

    const {
        openAddContactPane,
        systemsIsLoading,
        onCheckBoxClick,
        setHeaderCheckBoxState,
        onSelectAllEntities,
        onContactChange,
        onAdditionalRecipientsChange,
        onSystemChange,
        setShowSendAuditsDialog,
        showSendAuditsDialog,
        sendClicked,
        setSendClicked,
        auditIsLoading,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        searchString,
        setSearchString,
        searchedAudit,
        rows,
    } = useAuditGroupLegalEntitySelectorHooks(props);

    const snackbar = useDotLegalSnackbar();

    function getHeaders() {
        let headers = Array<ITableHeader<AuditGroupLegalEntityRowViewModel>>();
        headers.push({ property: "name", text: translateString("legalEntity"), align: "left", showOnMobile: true });
        headers.push({ property: "legalEntityTypesString", text: translateString("type"), align: "left", showOnMobile: true });
        headers.push({ property: "country", text: translateString("country"), align: "left", showOnMobile: true });
        headers.push({ property: "contactName", text: translateString("contact"), align: "left", showOnMobile: true });
        headers.push({ property: "additionalRecipientNames", text: translateString("additionalRecipients"), align: "left", showOnMobile: true });
        headers.push({ property: "systemNames", text: translateString("assets"), align: "left", showOnMobile: true });
        headers.push({ property: "classificationName", text: translateString("classifications"), align: "left", showOnMobile: true });
        headers.push({ property: "classificationRiskName", text: "", hideHeader: true });
        headers.push({ property: "auditName", text: translateString("latestAuditHeader"), align: "left", showOnMobile: true });
        headers.push({
            property: "auditSubmission",
            text: translateString("latestAuditSubmission"),
            align: "left",
            showOnMobile: true,
            sortOverride: (a: AuditGroupLegalEntityRowViewModel, b: AuditGroupLegalEntityRowViewModel, order) => sortLatesAuditSubmission(a, b, order),
        });
        headers.push({ property: "auditSubmissionName", text: "", hideHeader: true });
        headers.push({ property: "auditType", text: translateString("legalEntityAuditType"), align: "left", showOnMobile: true });
        headers.push({ property: "auditFrequency", text: translateString("legalEntityFrequency"), align: "left", showOnMobile: true });
        return headers;
    }

    function sortLatesAuditSubmission(a: AuditGroupLegalEntityRowViewModel, b: AuditGroupLegalEntityRowViewModel, order: "asc" | "desc") {
        if (order === "asc") {
            if (a.auditSubmission === null && b.auditSubmission === null) {
                if (a.auditSubmissionName > b.auditSubmissionName) return 1;
                else if (a.auditSubmissionName < b.auditSubmissionName) return -1;
                else return 0;
            } else if (a.auditSubmission !== null && b.auditSubmission !== null) {
                if (a.auditSubmission > b.auditSubmission) return 1;
                else if (a.auditSubmission < b.auditSubmission) return -1;
                return 0;
            } else if (a.auditSubmission !== null && b.auditSubmission === null) {
                return -1;
            }

            return 1;
        } else {
            if (a.auditSubmission === null && b.auditSubmission === null) {
                if (a.auditSubmissionName > b.auditSubmissionName) return -1;
                else if (a.auditSubmissionName < b.auditSubmissionName) return 1;
                else return 0;
            } else if (a.auditSubmission !== null && b.auditSubmission !== null) {
                if (a.auditSubmission > b.auditSubmission) return -1;
                else if (a.auditSubmission < b.auditSubmission) return 1;
                return 0;
            } else if (a.auditSubmission !== null && b.auditSubmission === null) {
                return -1;
            }

            return 1;
        }
    }

    function getFilterBar() {
        return (
            <FilterBar
                isLoading={props.selectedEntititesIsLoading}
                filterData={getFilterOptions()}
                fetchFilterData={!props.selectedEntititesIsLoading}
                onMenuClose={onFilterChange}
                onRemove={onFilterChange}
                pinnedFilterIds={pinnedFilterIds}
                onPinnedFilterIdsChange={onPinnedFilterIdsChange}
                searchValue={searchString}
                searchLabel={translateString("search")}
                onSearchChange={setSearchString}
            />
        );
    }

    const convertToSelectableItem = (contact: AuditGroupLegalEntityContact): DotLegalSelectOption => {
        return {
            id: contact.id,
            name: `${contact.name} ${contact.contactTypes.length > 0 ? `(${contact.contactTypes.map((x) => translateString(setFirstLetterToLowerCase(LegalEntityContactType[x].toString()))).join(", ")})` : ""}`,
            hoverText: contact.email,
        };
    };

    const formatLegalEntityTypes = (legalEntityTypes: Array<string>) => {
        return legalEntityTypes.map((x) => getLegalEntityTypeName(x)).join(", ");
    };

    function renderOptions(option: DotLegalSelectOption) {
        return (
            <DotLegalTooltip text={option.hoverText}>
                <Box>{option.name}</Box>
            </DotLegalTooltip>
        );
    }

    const getSelectedEntity = (legalEntityId: string) => {
        return props.selectedEntities.find((x) => x.legalEntityId === legalEntityId);
    };

    const getSelectedContactId = (row: AuditGroupLegalEntityRowApiModel) => {
        let selectedEntity = getSelectedEntity(row.id);

        if (selectedEntity) {
            return selectedEntity.contactId;
        }

        return row.contacts.find((x) => x.isPrimaryContact)?.id;
    };

    const getName = (row: AuditGroupLegalEntityRowApiModel) => {
        return (
            <Box key={row.id} component={"span"} sx={{ fontWeight: "bold" }}>
                <DotLegalLink linkColor={"primary"} to={getLegalEntityUrl(row.id)}>
                    {row.name}
                </DotLegalLink>
            </Box>
        );
    };

    function getSubmitted(audit?: LegalEntityAuditInformationViewModel) {
        if (audit) {
            if (audit.isClosed) {
                return translateString("excluded");
            }

            if (audit.submissionDate) {
                return translateDate(audit.submissionDate);
            }

            return translateString("notSubmitted");
        }

        return "";
    }

    function getLoadingState() {
        return <Skeleton width={"100%"} height={18} />;
    }

    const mapSelectedLegalEntities = (): Array<LegalEntityAuditGroupSendModel> => {
        let data: Array<LegalEntityAuditGroupSendModel> = [];

        props.selectedEntities.forEach((s) => {
            const entity = props.rows.find((x) => x.id === s.legalEntityId)!;

            const contact = entity.contacts.find((x) => x.id === s.contactId)!;
            const additionalRecipients = entity.contacts.filter((x) => s.additionalRecipientIds.includes(x.id));
            const systems = entity.assets.filter((x) => s.systemIds.includes(x.id));
            data.push({
                legalEntityId: entity.id,
                legalEntityName: entity.name,
                legalEntityTypes: entity.legalEntityTypes,
                countryCode: entity.countryCode,
                contactName: `${contact.name} ${contact.contactTypes.length > 0 ? ` (${contact.contactTypes.map((x) => translateString(setFirstLetterToLowerCase(LegalEntityContactType[x].toString()))).join(", ")})` : ""} `,
                contactEmail: contact.email,
                systemNames: systems.map((x) => x.name),
                systemIds: systems.map((x) => x.id),
                contactId: contact.id,
                additionalRecipientIds: additionalRecipients.map((x) => x.id),
                additionalRecipientNames: additionalRecipients.map((x) => x.name),
            });
        });

        return data;
    };

    return (
        <React.Fragment>
            <DotLegalPaper>
                <Box>
                    {props.selectedEntititesIsLoading || props.rows.length > 0 ? (
                        <>
                            {!vendorManagementFeatures.auditGroups && (
                                <TrialUsageWarning
                                    headerKey={translateString("auditGroupsLockedWarningHeader")}
                                    textKey={translateString("auditGroupsLockedWarning")}
                                    marginBottom={2}
                                />
                            )}

                            <ListPageToolbar leftContent={getFilterBar()} />
                            <DotLegalTable
                                headers={getHeaders()}
                                rowsPerPage={30}
                                searchString={searchString}
                                defaultOrderBy={"name"}
                                defaultOrder={"asc"}
                                isLoading={props.selectedEntititesIsLoading}
                                onSelectAllChanged={(selected) => onSelectAllEntities(selected)}
                                paginationLabelOf={translateString("labelOf")}
                                data={mapToAuditGroupLegalEntityRowViewModel(rows ?? [])}
                                clickableRows={false}
                                headerCheckBoxState={setHeaderCheckBoxState()}
                                emptyText={translateString("noData")}
                                disableCheckAll={!vendorManagementFeatures.auditGroups}
                                renderRow={(row) => {
                                    const selectedEntity = props.selectedEntities.find((x) => x.legalEntityId === row.id);
                                    const isSelected = selectedEntity !== undefined;
                                    const selectedContactId = getSelectedContactId(row);
                                    return (
                                        <TrialExpiredRowWrapper
                                            key={row.id}
                                            tooltip={translateString("auditGroupLocked")}
                                            onNonExpiredClick={() => {}}
                                            expired={!vendorManagementFeatures.auditGroups}
                                            planUpgradeText={translateString("auditGroupLocked")}
                                        >
                                            <TableCell>
                                                <DotLegalCheckbox
                                                    disabled={!vendorManagementFeatures.auditGroups}
                                                    onChange={() => onCheckBoxClick(row)}
                                                    checked={isSelected}
                                                />
                                            </TableCell>
                                            <TableCell>{getName(row)}</TableCell>
                                            <TableCell>{formatLegalEntityTypes(row.legalEntityTypes)}</TableCell>
                                            <TableCell>{translateCountry(row.countryCode)}</TableCell>
                                            <TableCell sx={{ minWidth: 250 }}>
                                                <Box
                                                    sx={(theme) => ({
                                                        visibility: isSelected ? "visible" : "hidden",
                                                        "& .MuiAutocomplete-root .MuiInputBase-input": {
                                                            marginBottom: -1,
                                                            fontSize: theme.typography.pxToRem(12),
                                                        },
                                                    })}
                                                >
                                                    <DotLegalSelectWithButton
                                                        variant="standard"
                                                        onButtonClick={() => openAddContactPane(row.id)}
                                                        buttonLabel={translateString("createNew")}
                                                        noOptionsLabel={translateString("noOptions")}
                                                        options={row.contacts.map((x) => convertToSelectableItem(x))}
                                                        onChange={(item) => onContactChange(row.id, item!)}
                                                        label={""}
                                                        placeholder={translateString("addContact")}
                                                        hasPermission
                                                        selectedItem={selectedContactId}
                                                        noMargin
                                                        renderOptions={renderOptions}
                                                        width="250"
                                                        errorText={
                                                            sendClicked && selectedEntity?.IsModelInvalid() ? (
                                                                <Typography sx={{ fontSize: 10 }}>{translateString("contactIsMandatory")}</Typography>
                                                            ) : (
                                                                ""
                                                            )
                                                        }
                                                        disableSearch
                                                        selectedValueText={row.contacts.find((x) => x.id === selectedContactId)?.email}
                                                        disabled={!vendorManagementFeatures.auditGroups}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 280 }}>
                                                <Box
                                                    sx={(theme) => ({
                                                        visibility: isSelected ? "visible" : "hidden",
                                                        "& .MuiAutocomplete-root .MuiInputBase-input": {
                                                            marginBottom: -1,
                                                            fontSize: theme.typography.pxToRem(12),
                                                        },
                                                    })}
                                                >
                                                    <DotLegalMultiSelect
                                                        variant="standard"
                                                        options={getAdditionalRecipientOptions(row.contacts, selectedContactId, translateString)}
                                                        chosenOptions={selectedEntity?.additionalRecipientIds ?? []}
                                                        onChange={(values) => onAdditionalRecipientsChange(row.id, values)}
                                                        label={""}
                                                        placeholder={translateString("addAdditionalRecipients")}
                                                        noOptionsLabel={translateString("noOptions")}
                                                        renderOptions={renderOptions}
                                                        width="280"
                                                        noMargin
                                                        disabled={!vendorManagementFeatures.auditGroups}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 280 }}>
                                                <Box
                                                    sx={(theme) => ({
                                                        visibility: isSelected ? "visible" : "hidden",
                                                        "& .MuiAutocomplete-root .MuiInputBase-input": {
                                                            marginBottom: -1,
                                                            fontSize: theme.typography.pxToRem(12),
                                                        },
                                                    })}
                                                >
                                                    <DotLegalMultiSelect
                                                        variant="standard"
                                                        options={row.assets}
                                                        chosenOptions={getSelectedEntity(row.id)?.systemIds ?? []}
                                                        onChange={(values) => onSystemChange(row.id, values)}
                                                        label={""}
                                                        placeholder={translateString("chooseOptionalAsset")}
                                                        noOptionsLabel={translateString("noOptions")}
                                                        isLoading={systemsIsLoading}
                                                        width="280"
                                                        noMargin
                                                        disabled={!vendorManagementFeatures.auditGroups}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 350 }}>
                                                {auditIsLoading
                                                    ? getLoadingState()
                                                    : row.classifications &&
                                                      row.classifications.length > 0 && (
                                                          <ClassificationRisk
                                                              legalEntityId={row.id}
                                                              legalEntityScope={LegalEntityScope.External}
                                                              classifications={row.classifications}
                                                          />
                                                      )}
                                            </TableCell>
                                            <TableCell width={200}>{auditIsLoading ? getLoadingState() : row.auditName}</TableCell>
                                            <TableCell width={220}>{auditIsLoading ? getLoadingState() : row.auditSubmissionName}</TableCell>
                                            <TableCell>
                                                {row.auditType
                                                    ? translateString(setFirstLetterToLowerCase(LegalEntityAuditType[row.auditType].toString()))
                                                    : undefined}
                                            </TableCell>
                                            <TableCell>
                                                {row.auditFrequency
                                                    ? translateString(setFirstLetterToLowerCase(LegalEntityAuditFrequency[row.auditFrequency].toString()))
                                                    : undefined}
                                            </TableCell>
                                        </TrialExpiredRowWrapper>
                                    );
                                }}
                            />
                        </>
                    ) : (
                        <DotLegalEmptyState icon={<NoData />} text={translateString("noLegalEntitiesToAddToAuditGroup")} />
                    )}
                </Box>
                {props.rows.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <PlanDialogButtonWrapper text={translateString("auditGroupLocked")} enabled={!vendorManagementFeatures.auditGroups}>
                            <DotLegalButton
                                disabled={props.selectedEntities.length === 0 || props.isDeadlineOverdue || !vendorManagementFeatures.auditGroups}
                                onClick={() => {
                                    setSendClicked(true);

                                    if (props.selectedEntities.some((x) => x.IsModelInvalid())) {
                                        snackbar.show(translateString("contactRequiredForLegalEntityOnAuditGroup"), "error");
                                        return;
                                    }

                                    setShowSendAuditsDialog(true);
                                }}
                                buttonType={"primary"}
                                toolTip={
                                    !vendorManagementFeatures.auditGroups
                                        ? translateString("auditGroupLocked")
                                        : props.isDeadlineOverdue
                                          ? translateString("auditGroupDeadlineIsOverdue")
                                          : undefined
                                }
                                endIcon={!vendorManagementFeatures.auditGroups ? WhiteLockIcon : undefined}
                            >
                                {translateString("sendAudits")} {props.selectedEntities.length > 0 && "(" + props.selectedEntities.length + ")"}
                            </DotLegalButton>
                        </PlanDialogButtonWrapper>
                    </Box>
                )}
            </DotLegalPaper>

            {showSendAuditsDialog && (
                <SendAuditsDialog
                    selectedEntities={mapSelectedLegalEntities()}
                    onDialogClose={() => setShowSendAuditsDialog(false)}
                    auditGroupId={props.auditGroupId}
                    auditGroupName={props.auditGroupName}
                />
            )}
        </React.Fragment>
    );

    function mapToAuditGroupLegalEntityRowViewModel(rows: Array<AuditGroupLegalEntityRowApiModel>): Array<AuditGroupLegalEntityRowViewModel> {
        return rows.map((row) => {
            let selectedEntity = getSelectedEntity(row.id);
            let searchAudit = row.audits?.find((x) => x.questionnaireId === searchedAudit);

            const hasClassiciations = row.classifications && row.classifications.length > 0;
            return {
                ...row,
                legalEntityTypesString: formatLegalEntityTypes(row.legalEntityTypes),
                country: translateCountry(row.countryCode),
                contactName: getSelectedContactName(row) ?? "",
                additionalRecipientNames: getSelectedAdditionalRecipientNames(row, selectedEntity),
                systemNames: getSelectedSystemNames(row, selectedEntity),
                classificationName: hasClassiciations ? row.classifications[0].name : "",
                classificationRiskName: hasClassiciations ? row.classifications[0].riskName : "",
                auditName: searchAudit ? searchAudit.name : (row.latestAudit?.name ?? ""),
                auditSubmission: getAuditSubmissionName(searchAudit, row.latestAudit),
                auditSubmissionName: getSubmitted(searchAudit ?? row.latestAudit),
                auditTypeName: row.auditType ? translateString(setFirstLetterToLowerCase(LegalEntityAuditType[row.auditType].toString())) : "",
                auditFrequencyName: row.auditFrequency
                    ? translateString(setFirstLetterToLowerCase(LegalEntityAuditFrequency[row.auditFrequency].toString()))
                    : "",
            };
        });
    }

    function getAuditSubmissionName(
        searchAudit: LegalEntityAuditInformationViewModel | undefined,
        latestAudit: LegalEntityAuditInformationViewModel | undefined
    ) {
        if (searchAudit) {
            return searchAudit?.submissionDate ? new Date(searchAudit?.submissionDate!) : null;
        }

        return latestAudit?.submissionDate ? new Date(latestAudit?.submissionDate!) : null;
    }

    function getSelectedContactName(row: AuditGroupLegalEntityRowApiModel) {
        let selectedEntity = getSelectedEntity(row.id);

        if (selectedEntity) {
            return row.contacts.find((x) => x.id === selectedEntity.contactId || x.isPrimaryContact)?.name;
        }

        return "";
    }

    function getSelectedAdditionalRecipientNames(row: AuditGroupLegalEntityRowApiModel, selectedEntity: LegalEntityAuditGroupDraft | undefined) {
        return row.contacts
            .filter((x) => selectedEntity?.additionalRecipientIds.includes(x.id))
            .map((x) => x.name)
            .join(", ");
    }

    function getSelectedSystemNames(row: AuditGroupLegalEntityRowApiModel, selectedEntity: LegalEntityAuditGroupDraft | undefined) {
        return row.assets
            .filter((x) => selectedEntity?.systemIds.includes(x.id))
            .map((x) => x.name)
            .join(", ");
    }
}

export default AuditGroupLegalEntitySelector;
